/**
 * ATMOSPHERE VIEW Stage 3 (docs/bim-plan/21 §4; graphic fidelity Round D, docs/bim-plan/18
 * §4): per-ELEMENT texture placement. Textures tile in world feet from the project
 * origin (bim-geometry uvs.ts), which is continuous across welded corners but lands a
 * floor's tile joint or a stone vein wherever the origin happens to put it. These three
 * instance params shift and turn the pattern on ONE element:
 *   - textureOffsetU / textureOffsetV: feet, along the face's own horizontal / vertical
 *     (floors: east / north);
 *   - textureRotationDeg: degrees, on top of the material's own rotation.
 * Applied in the worker when the element's surface coordinates are built
 * (pipeline.ts buildGroupChunk), so it costs nothing at draw time. Absent/0 = the
 * world-feet tiling exactly as before.
 */
import type { ParamDef } from './params'

/** The shared "Texture placement" instance group — walls, floors, ceilings. */
export const TEXTURE_PLACEMENT_GROUP: { label: string; params: ParamDef[] } = {
  label: 'Texture placement',
  params: [
    { key: 'textureOffsetU', label: 'Shift across', kind: 'length', default: 0, min: -50, max: 50 },
    { key: 'textureOffsetV', label: 'Shift up / along', kind: 'length', default: 0, min: -50, max: 50 },
    { key: 'textureRotationDeg', label: 'Rotate', kind: 'number', default: 0, min: -360, max: 360 },
  ],
}

/** the element's placement as a transform, or null when it is the identity */
export function texturePlacementOf(params: Record<string, unknown>): { du: number; dv: number; rotDeg: number } | null {
  const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0)
  const du = n(params.textureOffsetU), dv = n(params.textureOffsetV), rotDeg = n(params.textureRotationDeg)
  return du || dv || rotDeg ? { du, dv, rotDeg } : null
}
